import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LogOut, UserPlus, CalendarPlus, ArrowRight, XCircle, MessageCircle, DollarSign, Video, Pencil, Trash2, Search } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { Disponibilidade } from "@/components/Disponibilidade";
import { useAuth } from "@/store/AuthContext";
import { useClinic } from "@/store/ClinicContext";
import { DOCTORS, normBlocks, buildSlots, minutesOf } from "@/data/mockData";
import { INITIAL_DOCTORS } from "@/data/cfmData";
import { NotificationsBell } from "@/components/NotificationsBell";

const DAY_MAP = ["dom", "seg", "ter", "qua", "qui", "sex", "sab"];
const periodFromTime = (t) => (t && parseInt(t.split(":")[0], 10) < 12 ? "manha" : "tarde");
const periodLabel = (p) => (p === "manha" ? "manhã" : "tarde");

export default function Recepcao() {
  const { currentUser, logout } = useAuth();
  const { appointments, patients, addPatient, updatePatient, removePatient, addAppointment, advanceStatus, cancelAppointment, sendWhatsAppConfirmation, createCharge, syncCharge, availability, healthPlans } = useClinic();
  const navigate = useNavigate();

  const [patOpen, setPatOpen] = useState(false);
  const [editPatId, setEditPatId] = useState(null);
  const [dateFilter, setDateFilter] = useState("hoje");
  const [patSearch, setPatSearch] = useState("");
  const [pat, setPat] = useState({ name: "", rg: "", phone: "", email: "", cpf: "", data_nascimento: "", sexo: "", cep: "", address: "", number: "", complement: "", district: "", city: "", uf: "", convenio: "Particular" });
  const [aptOpen, setAptOpen] = useState(false);
  const planByName = (name) => healthPlans.find((p) => p.name === name);
  const valorFor = (conv) => Number(planByName(conv)?.valorConsulta) || 0;
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const isoToBR = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
  const weekdayISO = (iso) => new Date(`${iso}T00:00:00`).getDay();
  const [apt, setApt] = useState({ patientId: "", patient: "", doctorId: "carlos", time: "", procedure: "", convenio: "Particular", value: 0, date: new Date().toISOString().slice(0, 10) });

  const doLogout = () => { logout(); navigate("/login", { replace: true }); };

  const EMPTY_PAT = { name: "", rg: "", phone: "", email: "", cpf: "", data_nascimento: "", sexo: "", cep: "", address: "", number: "", complement: "", district: "", city: "", uf: "", convenio: "Particular" };

  const savePatient = async () => {
    if (!pat.name.trim()) return toast.error("Informe o nome do paciente.");
    if (editPatId) {
      await updatePatient(editPatId, pat);
      toast.success("Paciente atualizado!", { description: pat.name });
      setEditPatId(null);
      setPat({ ...EMPTY_PAT });
      setPatOpen(false);
      return;
    }
    const created = await addPatient(pat);
    toast.success("Paciente cadastrado!", { description: `${pat.name} · agora escolha a data do agendamento.` });
    const conv = pat.convenio && planByName(pat.convenio) ? pat.convenio : "Particular";
    setApt((a) => ({ ...a, patientId: created?.id || "", patient: pat.name, convenio: conv, value: valorFor(conv), date: todayISO(), time: "" }));
    setPat({ ...EMPTY_PAT });
    setPatOpen(false);
    setAptOpen(true);
  };

  const openNewPatient = () => { setEditPatId(null); setPat({ ...EMPTY_PAT }); setPatOpen(true); };
  const openEditPatient = (p) => {
    setEditPatId(p.id);
    setPat({ name: p.name || "", rg: p.rg || "", phone: p.phone || "", email: p.email || "", cpf: p.cpf || "", data_nascimento: p.data_nascimento || "", sexo: p.sexo || "", cep: p.cep || "", address: p.address || "", number: p.number || "", complement: p.complement || "", district: p.district || "", city: p.city || "", uf: p.uf || "", convenio: p.convenio || "Particular" });
    setPatOpen(true);
  };
  const deletePatient = async (p) => {
    await removePatient(p.id);
    toast("Paciente removido", { description: p.name });
  };

  const saveApt = () => {
    if (!apt.patient || !apt.time) return toast.error("Selecione paciente e horário.");
    if (!apt.date) return toast.error("Selecione a data do agendamento.");
    const who = DOCTORS.find((d) => d.id === apt.doctorId)?.shortName || "O médico";
    const conflict = appointments.some((a) => a.doctorId === apt.doctorId && a.time === apt.time && a.date === isoToBR(apt.date) && a.status !== "cancelado");
    if (conflict) {
      return toast.error("Horário já ocupado", { description: `${who} já tem um paciente às ${apt.time} em ${isoToBR(apt.date)}. Escolha outro horário.` });
    }
    const patientRec = patients.find((x) => x.id === apt.patientId);
    const freeReturn = patientRec?.return_status === "active";
    addAppointment({ ...apt, date: isoToBR(apt.date), patient_phone: patientRec?.phone || "", patient_email: patientRec?.email || "", doctor_name: who, procedure: apt.procedure || "Consulta", value: freeReturn ? 0 : (Number(apt.value) || 0), use_free_return: freeReturn });
    toast.success(freeReturn ? "Retorno gratuito agendado! (sem cobrança)" : "Agendamento criado!", { description: `${apt.patient} em ${isoToBR(apt.date)} às ${apt.time} · ${freeReturn ? "Retorno gratuito" : apt.convenio}` });
    setApt({ patientId: "", patient: "", doctorId: "carlos", time: "", procedure: "", convenio: "Particular", value: 0, date: todayISO() });
    setAptOpen(false);
  };

  const doctorName = (id) => DOCTORS.find((d) => d.id === id)?.shortName || "Médico";

  const sendWhatsApp = async (a) => {
    if (!a.patient_phone) return toast.error("Paciente sem telefone cadastrado.", { description: "Cadastre o telefone do paciente para enviar por WhatsApp." });
    try {
      await sendWhatsAppConfirmation(a);
      if (a.status === "pendente") advanceStatus(a.id);
      toast.success("Confirmação enviada por WhatsApp", {
        description: `Mensagem enviada para ${a.patient}. Aguardando resposta do paciente.`,
      });
    } catch (e) {
      toast.error("Falha ao enviar WhatsApp", { description: e.message });
    }
  };
  const brToDate = (br) => { const [d, m, y] = (br || "").split("/"); return y ? new Date(+y, +m - 1, +d) : null; };
  const inDateFilter = (a) => {
    if (dateFilter === "todos") return true;
    const dt = brToDate(a.date);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (!dt) return dateFilter === "hoje" || dateFilter === "semana";
    dt.setHours(0, 0, 0, 0);
    const diff = Math.round((dt - today) / 86400000);
    if (dateFilter === "hoje") return diff === 0;
    if (dateFilter === "amanha") return diff === 1;
    if (dateFilter === "semana") return diff >= 0 && diff <= 6;
    return true;
  };
  const visible = [...appointments].filter(inDateFilter).sort((a, b) => a.time.localeCompare(b.time));

  const filteredPatients = patients.filter((p) => {
    const q = patSearch.trim().toLowerCase();
    if (!q) return true;
    const digits = q.replace(/\D/g, "");
    return (p.name || "").toLowerCase().includes(q) || (digits && (p.cpf || "").replace(/\D/g, "").includes(digits));
  });

  const handleCep = async (v) => {
    setPat((p) => ({ ...p, cep: v }));
    const digits = v.replace(/\D/g, "");
    if (digits.length === 8) {
      try {
        const r = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
        const d = await r.json();
        if (!d.erro) setPat((p) => ({ ...p, address: d.logradouro || "", district: d.bairro || "", city: d.localidade || "", uf: d.uf || "" }));
      } catch (e) { /* ignore */ }
    }
  };

  const sendCharge = async (a) => {
    try {
      const d = await createCharge(a.id);
      const canais = [d.whatsapp_sent && "WhatsApp", d.email_sent && "e-mail"].filter(Boolean).join(" e ");
      toast.success("Cobrança enviada ao paciente", { description: canais ? `Link enviado por ${canais}.` : "Link gerado. Cadastre telefone/e-mail do paciente para envio automático." });
    } catch (e) {
      toast.error("Falha ao gerar cobrança", { description: e.message });
    }
  };
  const verificarPagamento = async (a) => {
    try {
      const d = await syncCharge(a.id);
      if (d.status === "paid") toast.success("Pagamento confirmado!", { description: "Consulta liberada para telemedicina." });
      else toast("Pagamento ainda pendente", { description: "Assim que o paciente pagar, a consulta será liberada automaticamente." });
    } catch (e) {
      toast.error("Não foi possível verificar", { description: e.message });
    }
  };

  const openTelemed = (a) => {
    if (a.telemed_room) window.open(`/sala/${a.telemed_room}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 py-3.5 md:px-8">
          <div className="flex items-center gap-3">
            <img src="/veloclin-logo.png" alt="VeloClin" data-testid="brand-logo" className="h-11 w-auto" />
            <span className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">Recepção</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm font-medium text-slate-500 sm:block">Olá, {currentUser?.name}</span>
            <NotificationsBell />
            <button data-testid="logout-btn" onClick={doLogout} className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-rose-50 hover:text-rose-600">
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 py-6 md:px-8 md:py-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Agenda Geral</h1>
            <p className="text-sm text-slate-500">Operação de recepção · {patients.length} pacientes cadastrados</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="new-patient-btn" onClick={openNewPatient} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"><UserPlus className="h-4 w-4 text-blue-600" /> Novo Paciente</button>
            <button data-testid="new-appointment-btn" onClick={() => setAptOpen(true)} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"><CalendarPlus className="h-4 w-4" /> Novo Agendamento</button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Agenda */}
          <div className="lg:col-span-2 overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <div className="flex flex-wrap gap-1.5 border-b border-slate-100 px-5 py-3">
              {[["hoje", "Hoje"], ["amanha", "Amanhã"], ["semana", "Semana"], ["todos", "Todas"]].map(([k, label]) => (
                <button
                  key={k}
                  data-testid={`agenda-filter-${k}`}
                  onClick={() => setDateFilter(k)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${dateFilter === k ? "bg-blue-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  {label}
                </button>
              ))}
              <span className="ml-auto self-center text-xs text-slate-400">{visible.length} agendamento(s)</span>
            </div>
            <div className="divide-y divide-slate-100">
              {visible.length === 0 && (
                <p data-testid="agenda-empty" className="py-10 text-center text-sm text-slate-400">Nenhum agendamento neste período.</p>
              )}
              {visible.map((a) => (
                <div key={a.id} data-testid={`recep-row-${a.id}`} className="flex flex-wrap items-center gap-3 px-5 py-3.5 hover:bg-slate-50/80">
                  <span className="font-mono-num text-lg font-bold text-slate-900">{a.time}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-slate-900">{a.patient}</p>
                    <p className="truncate text-xs text-slate-500">{a.procedure} · {doctorName(a.doctorId)}{a.date ? ` · ${a.date}` : ""}</p>
                  </div>
                  {a.is_return && <span data-testid={`recep-return-${a.id}`} className="inline-flex shrink-0 items-center rounded-md border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">Retorno grátis</span>}
                  {a.convenio && <span data-testid={`recep-convenio-${a.id}`} className="inline-flex shrink-0 items-center rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">{a.convenio}</span>}
                  <StatusBadge status={a.status} />
                  {a.charge_status === "paid" && (
                    <button data-testid={`recep-telemed-${a.id}`} onClick={() => openTelemed(a)} className="flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100" title="Abrir teleconsulta"><Video className="h-3.5 w-3.5" /> Telemedicina</button>
                  )}
                  {a.status !== "cancelado" && a.status !== "atendido" && (
                    <div className="flex gap-1.5">
                      <button data-testid={`recep-whatsapp-${a.id}`} onClick={() => sendWhatsApp(a)} className="flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100" title="Confirmar por WhatsApp"><MessageCircle className="h-3.5 w-3.5" /></button>
                      <button data-testid={`recep-charge-${a.id}`} onClick={() => sendCharge(a)} className="flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100" title="Gerar cobrança e enviar link"><DollarSign className="h-3.5 w-3.5" /></button>
                      {a.charge_status === "pending" && <button data-testid={`recep-verify-${a.id}`} onClick={() => verificarPagamento(a)} className="flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100" title="Verificar pagamento"><ArrowRight className="h-3.5 w-3.5" /></button>}
                      <button data-testid={`recep-advance-${a.id}`} onClick={() => advanceStatus(a.id)} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><ArrowRight className="h-3.5 w-3.5" /></button>
                      <button data-testid={`recep-cancel-${a.id}`} onClick={() => { cancelAppointment(a.id); toast("Consulta cancelada", { description: a.patient }); }} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-rose-50 hover:text-rose-600"><XCircle className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Pacientes cadastrados + Disponibilidade dos médicos (somente leitura) */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-bold text-slate-900">Pacientes cadastrados</p>
                <span data-testid="recep-patients-count" className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">{patients.length}</span>
              </div>
              {patients.length > 0 && (
                <div className="relative mb-3">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="recep-patient-search" value={patSearch} onChange={(e) => setPatSearch(e.target.value)} placeholder="Buscar por nome ou CPF..." className="pl-8" />
                </div>
              )}
              {patients.length === 0 ? (
                <p data-testid="recep-patients-empty" className="py-4 text-center text-xs text-slate-400">Nenhum paciente cadastrado ainda. Clique em “Novo Paciente”.</p>
              ) : filteredPatients.length === 0 ? (
                <p data-testid="recep-patients-no-match" className="py-4 text-center text-xs text-slate-400">Nenhum paciente encontrado para “{patSearch}”.</p>
              ) : (
                <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
                  {filteredPatients.map((p) => (
                    <div key={p.id} data-testid={`recep-patient-${p.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-800">{p.name}</p>
                        <p className="truncate text-xs text-slate-400">{p.phone || "sem telefone"} · {p.convenio}</p>
                        {p.return_status === "active" && <p data-testid={`recep-patient-return-${p.id}`} className="truncate text-[11px] font-semibold text-emerald-600">Retorno gratuito ativo</p>}
                      </div>
                      <div className="flex shrink-0 gap-1.5">
                        <button
                          data-testid={`recep-schedule-${p.id}`}
                          onClick={() => {
                            const conv = p.convenio && planByName(p.convenio) ? p.convenio : "Particular";
                            setApt((a) => ({ ...a, patientId: p.id, patient: p.name, convenio: conv, value: valorFor(conv), date: todayISO(), time: "" }));
                            setAptOpen(true);
                          }}
                          className="rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs font-bold text-white transition-colors hover:bg-blue-700"
                        >
                          Agendar
                        </button>
                        <button data-testid={`recep-edit-${p.id}`} onClick={() => openEditPatient(p)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-slate-500 transition-colors hover:bg-slate-50" title="Editar"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`recep-delete-${p.id}`} onClick={() => deletePatient(p)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-600" title="Excluir"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {INITIAL_DOCTORS.map((d) => (
              <div key={d.id} className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                <div className="mb-3 flex items-center gap-2.5">
                  <img src={d.avatar} alt={d.name} className="h-8 w-8 rounded-full object-cover" />
                  <div><p className="text-sm font-bold text-slate-900">{d.name}</p><p className="text-xs text-slate-400">{d.specialty}</p></div>
                </div>
                <Disponibilidade doctorId={d.id} readOnly />
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Modal paciente */}
      <Dialog open={patOpen} onOpenChange={setPatOpen}>
        <DialogContent data-testid="patient-modal" className="max-w-md">
          <DialogHeader><DialogTitle>{editPatId ? "Editar Paciente" : "Novo Paciente"}</DialogTitle><DialogDescription>{editPatId ? "Atualize os dados do paciente." : "Cadastre um paciente para agendar."}</DialogDescription></DialogHeader>
          <div className="max-h-[70vh] space-y-3 overflow-y-auto py-2 pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-slate-600">Nome</Label><Input data-testid="patient-name-input" value={pat.name} onChange={(e) => setPat({ ...pat, name: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">RG</Label><Input data-testid="patient-rg-input" value={pat.rg} onChange={(e) => setPat({ ...pat, rg: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-slate-600">Telefone / WhatsApp</Label><Input data-testid="patient-phone-input" value={pat.phone} onChange={(e) => setPat({ ...pat, phone: e.target.value })} className="mt-1.5" placeholder="(11) 90000-0000" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">E-mail</Label><Input data-testid="patient-email-input" type="email" value={pat.email} onChange={(e) => setPat({ ...pat, email: e.target.value })} className="mt-1.5" placeholder="paciente@email.com" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs font-semibold text-slate-600">CPF</Label><Input data-testid="patient-cpf-input" value={pat.cpf} onChange={(e) => setPat({ ...pat, cpf: e.target.value })} className="mt-1.5" placeholder="000.000.000-00" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Nascimento</Label><Input data-testid="patient-birth-input" value={pat.data_nascimento} onChange={(e) => setPat({ ...pat, data_nascimento: e.target.value })} className="mt-1.5" placeholder="DD/MM/AAAA" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Sexo</Label>
                <Select value={pat.sexo} onValueChange={(v) => setPat({ ...pat, sexo: v })}>
                  <SelectTrigger data-testid="patient-sexo-select" className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="M">Masculino</SelectItem><SelectItem value="F">Feminino</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs font-semibold text-slate-600">CEP</Label><Input data-testid="patient-cep-input" value={pat.cep} onChange={(e) => handleCep(e.target.value)} inputMode="numeric" placeholder="00000-000" className="mt-1.5" /></div>
              <div className="col-span-2"><Label className="text-xs font-semibold text-slate-600">Endereço</Label><Input data-testid="patient-address-input" value={pat.address} onChange={(e) => setPat({ ...pat, address: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs font-semibold text-slate-600">Número</Label><Input data-testid="patient-number-input" value={pat.number} onChange={(e) => setPat({ ...pat, number: e.target.value })} className="mt-1.5" /></div>
              <div className="col-span-2"><Label className="text-xs font-semibold text-slate-600">Complemento</Label><Input data-testid="patient-complement-input" value={pat.complement} onChange={(e) => setPat({ ...pat, complement: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs font-semibold text-slate-600">Bairro</Label><Input data-testid="patient-district-input" value={pat.district} onChange={(e) => setPat({ ...pat, district: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Cidade</Label><Input data-testid="patient-city-input" value={pat.city} onChange={(e) => setPat({ ...pat, city: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">UF</Label><Input data-testid="patient-uf-input" value={pat.uf} onChange={(e) => setPat({ ...pat, uf: e.target.value.toUpperCase().slice(0, 2) })} className="mt-1.5" /></div>
            </div>
            <div><Label className="text-xs font-semibold text-slate-600">Convênio</Label>
              <Select value={pat.convenio} onValueChange={(v) => setPat({ ...pat, convenio: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{healthPlans.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setPatOpen(false)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
            <button data-testid="patient-save-btn" onClick={savePatient} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700">{editPatId ? "Salvar" : "Cadastrar"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal agendamento */}
      <Dialog open={aptOpen} onOpenChange={setAptOpen}>
        <DialogContent data-testid="appointment-modal" className="max-w-md">
          <DialogHeader><DialogTitle>Novo Agendamento / Encaixe</DialogTitle><DialogDescription>Aloque um paciente na agenda de um médico.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs font-semibold text-slate-600">Paciente</Label>
              <Select value={apt.patientId} onValueChange={(v) => {
                const p = patients.find((x) => x.id === v);
                const conv = p?.convenio && planByName(p.convenio) ? p.convenio : "Particular";
                setApt({ ...apt, patientId: v, patient: p?.name || "", convenio: conv, value: valorFor(conv) });
              }}>
                <SelectTrigger data-testid="apt-patient-select" className="mt-1.5"><SelectValue placeholder="Selecione o paciente" /></SelectTrigger>
                <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-semibold text-slate-600">Médico</Label>
              <Select value={apt.doctorId} onValueChange={(v) => setApt({ ...apt, doctorId: v })}>
                <SelectTrigger data-testid="apt-doctor-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{INITIAL_DOCTORS.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-semibold text-slate-600">Data</Label><Input data-testid="apt-date-input" type="date" value={apt.date} min={todayISO()} onChange={(e) => setApt({ ...apt, date: e.target.value, time: "" })} className="mt-1.5" /></div>
            {(() => {
              const day = DAY_MAP[weekdayISO(apt.date)];
              const av = availability[apt.doctorId]?.[day] || {};
              const m = normBlocks(av.manha, "manha");
              const t = normBlocks(av.tarde, "tarde");
              const fmt = (arr) => arr.map((b) => `${b.from}–${b.to}`).join(", ");
              const parts = [m.length && `Manhã ${fmt(m)}`, t.length && `Tarde ${fmt(t)}`].filter(Boolean);
              const slots = buildSlots([...m, ...t], 30);
              const taken = new Set(appointments.filter((a) => a.doctorId === apt.doctorId && a.date === isoToBR(apt.date) && a.status !== "cancelado").map((a) => a.time));
              return (
                <>
                  <div data-testid="apt-availability-hint" className="rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs text-blue-700">
                    Você pode escolher qualquer horário em {isoToBR(apt.date)}.{parts.length ? ` Agenda aberta ao público: ${parts.join(" · ")}.` : ""} Apenas conflitos de horário são bloqueados.
                  </div>
                  {slots.length > 0 && (
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Horários disponíveis (30 min)</Label>
                      <div data-testid="slot-grid" className="mt-1.5 flex flex-wrap gap-1.5">
                        {slots.map((s) => {
                          const busy = taken.has(s);
                          return (
                            <button
                              key={s}
                              type="button"
                              data-testid={`slot-${s}`}
                              disabled={busy}
                              onClick={() => setApt({ ...apt, time: s })}
                              className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${busy ? "cursor-not-allowed bg-slate-100 text-slate-300 line-through" : apt.time === s ? "bg-blue-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-blue-50"}`}
                            >
                              {s}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
            <div><Label className="text-xs font-semibold text-slate-600">Convênio</Label>
              <Select value={apt.convenio} onValueChange={(v) => setApt({ ...apt, convenio: v, value: valorFor(v) })}>
                <SelectTrigger data-testid="apt-convenio-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{healthPlans.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-slate-600">Horário</Label><Input data-testid="apt-time-input" type="time" value={apt.time} onChange={(e) => setApt({ ...apt, time: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Valor da consulta (R$)</Label><Input data-testid="apt-value-input" type="number" value={apt.value} onChange={(e) => setApt({ ...apt, value: Number(e.target.value) })} className="mt-1.5" /></div>
            </div>
            <div><Label className="text-xs font-semibold text-slate-600">Procedimento</Label><Input data-testid="apt-proc-input" value={apt.procedure} onChange={(e) => setApt({ ...apt, procedure: e.target.value })} className="mt-1.5" placeholder="Consulta" /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setAptOpen(false)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
            <button data-testid="apt-save-btn" onClick={saveApt} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700">Agendar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
